import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useHistory, useRouteMatch, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { AxiosError } from "axios";
import { queryCache, QueryKey, useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";

import {
  Banner,
  Button,
  Dialog,
  Loader,
  showNotification,
  TextInput,
} from "fe-shared/src/components";
import useDialog from "fe-shared/src/hooks/useDialog";
import useInvalidationControls from "fe-shared-transactions/src/hooks/useInvalidationControls";
import {
  CloseIcon,
  ReturnIcon,
  CheckIcon,
} from "fe-shared/src/components/Icons";
import { ApiError } from "fe-shared/src/types/common";
import { PermissionLevel, UserPermission } from "fe-shared/src/types/domain";
import {
  TRANSACTION_EDIT_BUTTON_PORTAL_ID,
  DISPOSITION_EDIT_BUTTON_PORTAL_ID,
} from "fe-shared-transactions/src/consts";
import {
  EditDispositionParams,
  Transaction,
  TransactionMaterial,
  TransactionStatus as TransactionStatusEnum,
  TransactionType,
  CreateReceiptFormValues,
} from "fe-shared-transactions/src/types";
import { RevertIcon } from "fe-shared-transactions/src/components/Icons";
import {
  DialogHeader,
  CorrectionsDialog,
  SectionEditAction,
  EditDispositionDialog,
  UnidentifiedMaterialsBanner,
  InvalidationControls,
  CreateReceiptDialog,
} from "fe-shared-transactions/src/components";
import { TransactionAction } from "types";
import { TRANSACTION_DETAILS_PORTAL_ID, PERMISSIONS } from "consts";

import { actions, ActionConfig } from "../modules/config";
import * as api from "../modules/api";
import styles from "../TransactionDetailsView.module.scss";

interface Props {
  transaction: Transaction;
  activitiesQueryKey?: QueryKey;
  transactionDetailsQueryKey?: QueryKey;
  isPermitted: (permission: UserPermission | UserPermission[]) => boolean;
  isLoadingTransaction: boolean;
  isCurrentUserAssignee: boolean;
  isInitiatedDelivery?: boolean;
}

type ToastIcons = {
  [key in TransactionAction]?: React.ElementType;
};

const {
  Invalidate,
  Restore,
  Return,
  Approve,
  CreateAdjustment,
  EditAdjustment,
  ApproveAdjustment,
  EditDisposition,
} = TransactionAction;

const editActionsArray = [EditAdjustment, EditDisposition];

const toastIcons: ToastIcons = {
  [Invalidate]: CloseIcon,
  [Restore]: RevertIcon,
  [Return]: ReturnIcon,
  [Approve]: CheckIcon,
  [CreateAdjustment]: CheckIcon,
  [ApproveAdjustment]: CheckIcon,
};

const TransactionActionsContainer = (props: Props) => {
  const {
    isPermitted,
    transaction,
    activitiesQueryKey,
    transactionDetailsQueryKey,
    isLoadingTransaction,
    isCurrentUserAssignee,
    isInitiatedDelivery,
  } = props;

  const history = useHistory();
  const match = useRouteMatch();
  const location = useLocation();

  const [
    editButtonContainer,
    setEditButtonContainer,
  ] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const portalId =
      transaction.transactionType === TransactionType.Disposition
        ? DISPOSITION_EDIT_BUTTON_PORTAL_ID
        : TRANSACTION_EDIT_BUTTON_PORTAL_ID;
    const element = document.getElementById(portalId);

    if (element) {
      setEditButtonContainer(element as HTMLDivElement);
    }
  }, [transaction.transactionType]);

  const { t } = useTranslation();
  const [activeAction, setActiveAction] = useState(actions[0]);
  const [materials, setMaterials] = useState<TransactionMaterial[]>([]);
  const [warningMessage, setWarningMessage] = useState("");
  const {
    isReasonIncorrect,
    invalidationReason,
    isShowToParticipant,
    ...invalidationProps
  } = useInvalidationControls();

  const {
    isDialogActive,
    isDialogRendered,
    openDialog,
    closeDialog,
  } = useDialog();

  const {
    isDialogActive: isCreateReceiptDialogActive,
    isDialogRendered: isCreateReceiptDialogRendered,
    openDialog: openCreateReceiptDialog,
    closeDialog: closeCreateReceiptDialog,
  } = useDialog();

  const {
    isDialogActive: isCancelDialogActive,
    isDialogRendered: isCancelDialogRendered,
    openDialog: openCancelDialog,
    closeDialog: closeCancelDialog,
  } = useDialog();

  const [cancelReason, setCancelReason] = useState('');

  const { name: activeActionName } = activeAction;

  const visibleActions = useMemo(
    () =>
      actions
        .filter((action) =>
          action.isVisible(transaction, isCurrentUserAssignee)
        )
        .filter(({ permission }) =>
          isPermitted(permission(transaction.transactionType))
        ),
    [transaction, isPermitted, isCurrentUserAssignee]
  );

  const visibleInFooterActions = useMemo(
    () => visibleActions.filter(({ name }) => !editActionsArray.includes(name)),
    [visibleActions]
  );

  const editActionConfig = useMemo(
    () =>
      visibleActions.find((action) => editActionsArray.includes(action.name)),
    [visibleActions]
  );

  const isInvalidationAction = activeActionName === Invalidate;

  const isAdjustmentActions = [CreateAdjustment, EditAdjustment].includes(
    activeActionName
  );

  const isAdjustment =
    transaction.transactionType === TransactionType.Adjustment;

  const isApproveAction = activeActionName === Approve;
  const isDispositionAction = activeActionName === EditDisposition;
  const isDefaultDialogUsed =
    !isAdjustmentActions && !isDispositionAction && !isApproveAction;

  const getMutationConfig = (action: TransactionAction) => ({
    onSuccess: () => {
      const iconComponent = toastIcons[action];

      queryCache.invalidateQueries(transactionDetailsQueryKey);
      queryCache.invalidateQueries(activitiesQueryKey);

      showNotification(
        t(`actions.${action}.toastMessage`),
        undefined,
        iconComponent
      );
    },
  });

  const mutations = {
    [Invalidate]: useMutation<unknown, AxiosError<ApiError>>(
      () =>
        api.invalidateTransaction({
          transactionId: transaction.transactionId,
          reason: invalidationReason,
          showToParticipant: isShowToParticipant,
          isAdjustment,
        }),
      getMutationConfig(Invalidate)
    ),
    [Restore]: useMutation<unknown, AxiosError<ApiError>>(
      () =>
        api.restoreInvalidatedTransaction({
          transactionId: transaction.transactionId,
          isAdjustment,
        }),
      getMutationConfig(Restore)
    ),
    [Return]: useMutation<unknown, AxiosError<ApiError>>(
      () =>
        api.returnTransaction({
          transactionId: transaction.transactionId,
        }),
      getMutationConfig(Return)
    ),
    [Approve]: useMutation<TransactionMaterial[], AxiosError<ApiError>>(
      () => api.approveTransaction(transaction),
      {
        onSuccess: (unidentifiedRouteMaterials) => {
          if (unidentifiedRouteMaterials.length) {
            setActiveAction(actions.find((a) => a.name === Approve)!);
            setMaterials(unidentifiedRouteMaterials);
            openDialog();
          } else {
            getMutationConfig(Approve).onSuccess();
          }
        },
        onError: (err) => {
          showNotification(t(err.message), undefined, CloseIcon);
        },
      }
    ),
    [CreateAdjustment]: useMutation<
      api.CreateAdjustmentResponse,
      AxiosError<ApiError>,
      api.CreateAdjustmentParams
    >(api.createAdjustment, {
      onSuccess: (data) => {
        getMutationConfig(CreateAdjustment).onSuccess();
        history.push(
          `${match.path.replace(
            ":transactionNumber",
            data.adjustmentNumber
          )}?navigation=true`
        );
      },
    }),
    [EditAdjustment]: useMutation<
      unknown,
      AxiosError<ApiError>,
      api.CreateAdjustmentParams
    >(api.editAdjustment, getMutationConfig(EditAdjustment)),
    [ApproveAdjustment]: useMutation<unknown, AxiosError<ApiError>>(
      () => api.approveTransaction(transaction),
      getMutationConfig(Approve)
    ),
    [EditDisposition]: useMutation<
      unknown,
      AxiosError<ApiError>,
      EditDispositionParams
    >(api.editDisposition, getMutationConfig(EditDisposition)),
  };

  const [applyCreateReceipt, createReceiptResult] = useMutation<
    void,
    AxiosError<ApiError>,
    CreateReceiptFormValues
  >(
    (values) => api.createReceipt(transaction.transactionId, values),
    {
      onSuccess: () => {
        queryCache.invalidateQueries(transactionDetailsQueryKey);
        queryCache.invalidateQueries(activitiesQueryKey);
        closeCreateReceiptDialog();
        showNotification(t("transactions.actions.createReceiptSuccess"));
      },
      onError: () => {
        showNotification(t("transactions.actions.createReceiptError"), undefined, CloseIcon);
      },
    }
  );

  const { isLoading: isCreateReceiptLoading } = createReceiptResult;

  const canCreateReceipt = isInitiatedDelivery && isPermitted({
    name: PERMISSIONS.transactionCreateReceipt,
    level: PermissionLevel.Full,
  });

  const canCancel = isInitiatedDelivery && isPermitted({
    name: PERMISSIONS.transactionInvalidation,
    level: PermissionLevel.Full,
  });

  const [applyCancelDelivery, cancelDeliveryResult] = useMutation<void, AxiosError<ApiError>>(
    () => api.cancelDeliveryTransaction(transaction.transactionNumber, cancelReason),
    {
      onSuccess: () => {
        showNotification(t("transactions.actions.cancelSuccess"));
        window.location.reload();
      },
      onError: () => {
        showNotification(t("transactions.actions.cancelError"), undefined, CloseIcon);
      },
    }
  );

  const { isLoading: isCancelDeliveryLoading } = cancelDeliveryResult;

  const [applyAction, mutationResult] = mutations[activeActionName];
  const { isLoading, isSuccess, reset } = mutationResult;

  const dialogTitle = (
    <DialogHeader
      type={transaction.transactionType}
      title={t(`actions.${activeActionName}.dialogTitle`)}
      number={transaction.transactionNumber}
      translationRoot="transactions"
    />
  );

  const dialogActions = [
    <Button
      key="1"
      id={`transaction-action-dialog-${activeActionName}-button`}
      disabled={isInvalidationAction && isReasonIncorrect}
      onClick={() => applyAction()}
      {...activeAction?.buttonProps}
      {...activeAction?.dialogButtonProps}
    >
      {t(`actions.${activeActionName}.buttonLabel`)}
    </Button>,
    <Button
      key="2"
      variant="outlined"
      onClick={closeDialog}
      id="transaction-action-dialog-cancel-button"
    >
      {t("cancel")}
    </Button>,
  ];

  const approveActions = [
    <Button key="1" onClick={closeDialog} id="transaction-action-dialog-close-button">
      {t("close")}
    </Button>,
  ];

  useEffect(() => {
    if (!isLoading && isSuccess) {
      closeDialog();
      reset();
    }
  }, [isLoading, isSuccess, reset, closeDialog]);

  const dismissWarning = () => setWarningMessage("");

  useEffect(() => {
    visibleActions.forEach((item) => {
      if (
        !item.isDisabled?.(transaction) &&
        warningMessage === t(`actions.${item.name}.warningMessage`)
      ) {
        dismissWarning();
      }
    });
  }, [transaction, visibleActions, warningMessage, t]);

  useEffect(() => {
    dismissWarning();
  }, [location]);

  const applyDialogAction = (action: ActionConfig) => {
    setActiveAction(action);
    openDialog();
  };

  const hasAnyButtons = visibleActions.length || canCreateReceipt || canCancel;

  return hasAnyButtons ? (
    <>
      <div className={styles.actions}>
        {canCreateReceipt && (
          <>
            <Button
              id="transaction-action-create-receipt-button"
              className={styles.button}
              disabled={isLoadingTransaction}
              onClick={openCreateReceiptDialog}
            >
              {t("transactions.actions.createReceipt")}
            </Button>
          </>
        )}
        {canCancel && (
          <Button
            id="transaction-action-cancel-button"
            className={styles.invalidateButton}
            disabled={isLoadingTransaction || isCancelDeliveryLoading}
            onClick={openCancelDialog}
          >
            {t("transactions.actions.cancel")}
          </Button>
        )}
        {visibleInFooterActions.map((action) => {
          const {
            name,
            buttonProps,
            hasConfirmationDialog,
            isDisabled,
          } = action;
          const [
            applyInstantAction,
            { isLoading: isActionLoading },
          ] = mutations[name];

          return (
            <Fragment key={name}>
              <Button
                id={`transaction-action-${name}-button`}
                className={styles.button}
                disabled={isActionLoading || isLoadingTransaction}
                onClick={() => {
                  if (isDisabled?.(transaction)) {
                    dismissWarning();
                    requestAnimationFrame(() => {
                      setWarningMessage(t(`actions.${name}.warningMessage`));
                    });
                  } else if (hasConfirmationDialog) {
                    applyDialogAction(action);
                  } else {
                    applyInstantAction();
                  }
                }}
                {...buttonProps}
              >
                {t(`actions.${name}.buttonLabel`)}
              </Button>
              {isActionLoading && !hasConfirmationDialog && (
                <Loader isBlocking />
              )}
            </Fragment>
          );
        })}
      </div>
      {isDefaultDialogUsed && isDialogRendered && (
        <Dialog
          modal
          title={dialogTitle}
          actions={dialogActions}
          isActive={isDialogActive}
          onClose={closeDialog}
        >
          {isLoading && <Loader isBlocking />}
          {isInvalidationAction ? (
            <InvalidationControls
              invalidationReason={invalidationReason}
              isShowToParticipant={isShowToParticipant}
              isWithCheckbox={
                ![
                  TransactionType.Adjustment,
                  TransactionType.Retreading,
                ].includes(transaction.transactionType)
              }
              {...invalidationProps}
            />
          ) : (
            <span className={styles.confirmationMessage}>
              {t(`actions.${activeActionName}.confirmationMessage`)}
            </span>
          )}
        </Dialog>
      )}
      {isApproveAction && isDialogRendered && (
        <Dialog
          modal
          title={dialogTitle}
          actions={approveActions}
          isActive={isDialogActive}
          onClose={closeDialog}
        >
          <UnidentifiedMaterialsBanner
            isInvoice={false}
            materials={materials}
            className={styles.banner}
          />
        </Dialog>
      )}
      {isAdjustmentActions && isDialogRendered && (
        <CorrectionsDialog
          transaction={transaction}
          corrections={null}
          isAdjustment
          applyAction={applyAction as (arg: unknown) => void}
          isActive={isDialogActive}
          isLoading={isLoading}
          dialogHeader={dialogTitle}
          onClose={closeDialog}
        />
      )}
      {isDispositionAction && isDialogRendered && (
        <EditDispositionDialog
          isActive={isDialogActive}
          dialogTitle={dialogTitle}
          onClose={closeDialog}
          transaction={transaction}
          activitiesQueryKey={activitiesQueryKey}
          transactionDetailsQueryKey={transactionDetailsQueryKey}
        />
      )}
      {isCreateReceiptDialogRendered && (
        <CreateReceiptDialog
          transaction={transaction}
          isActive={isCreateReceiptDialogActive}
          onClose={closeCreateReceiptDialog}
          onSubmit={(values) => applyCreateReceipt(values)}
          isLoading={isCreateReceiptLoading}
        />
      )}
      {isCancelDialogRendered && (
        <Dialog
          modal
          title={
            <DialogHeader
              type={transaction.transactionType}
              number={transaction.transactionNumber}
              title={t("transactions.actions.cancelDialogTitle")}
              translationRoot="transactions"
            />
          }
          isActive={isCancelDialogActive}
          onClose={() => {
            setCancelReason('');
            closeCancelDialog();
          }}
          actions={[
            <Button
              key="confirm"
              id="cancel-dialog-confirm-button"
              className={styles.invalidateButton}
              disabled={isCancelDeliveryLoading || !cancelReason.trim()}
              onClick={() => applyCancelDelivery()}
            >
              {t("transactions.actions.cancelDialogTitle")}
            </Button>,
            <Button
              key="close"
              variant="outlined"
              id="cancel-dialog-close-button"
              onClick={() => {
                setCancelReason('');
                closeCancelDialog();
              }}
            >
              {t("cancel")}
            </Button>,
          ]}
        >
          {isCancelDeliveryLoading && <Loader isBlocking />}
          <TextInput
            isMultiline
            id="cancel-dialog-reason-input"
            title={t("transactions.actions.cancelReason")}
            value={cancelReason}
            onChange={(e, value) => setCancelReason(value)}
          />
        </Dialog>
      )}
      {warningMessage &&
        createPortal(
          <Banner
            variant="warning"
            title={<ReactMarkdown source={warningMessage} />}
            id="transaction-action-warning-banner"
            onDismiss={dismissWarning}
          />,
          document.getElementById(TRANSACTION_DETAILS_PORTAL_ID)!
        )}
      {!!editButtonContainer &&
        !!editActionConfig &&
        createPortal(
          <SectionEditAction
            canEdit
            editCallback={() => applyDialogAction(editActionConfig)}
            actionName={editActionConfig.name}
          />,
          editButtonContainer
        )}
    </>
  ) : null;
};

export default TransactionActionsContainer;
